import React, { startTransition, useDeferredValue, useState } from "react";
import { createPortal } from "react-dom";
import FleetCard from "./FleetCard.jsx";
import FleetFilterBar from "./FleetFilterBar.jsx";
import { DISPATCH_FRAME_RADIUS_CLASS_NAME, EmptyState } from "./ResultsPrimitives.jsx";
import { buildFleetSearchValue } from "../utils/resultsFleetCardShell.js";

function normalizeFleetSearch(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function buildCountLabel(visibleCount, totalCount, hasQuery) {
  if (hasQuery && visibleCount !== totalCount) {
    return `${visibleCount}/${totalCount} visibles`;
  }

  return `${visibleCount} visibles`;
}

export default function FleetList({
  cards = [],
  mountNode = null,
  routeCanvasStepsPanel = false,
  selectedHeaderModel = null,
}) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeFleetSearch(deferredQuery);
  const hasQuery = normalizedQuery.length > 0;
  const items = Array.isArray(cards) ? cards.filter(Boolean) : [];

  const visibleCards = items.filter((card) => {
    if (!hasQuery) {
      return true;
    }

    const searchValue = normalizeFleetSearch(buildFleetSearchValue(card?.assignment));

    return searchValue.includes(normalizedQuery);
  });

  if (!(mountNode instanceof HTMLElement)) {
    return null;
  }

  return createPortal(
    <div
      className={`grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-1.25 overflow-hidden border border-[rgba(148,163,184,0.18)] bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(248,250,252,0.94))] p-1.5 shadow-[0_22px_44px_rgba(15,23,42,0.08)] backdrop-blur-[18px] ${DISPATCH_FRAME_RADIUS_CLASS_NAME}`}
      data-results-fleet-dock-panel="true"
    >
      <FleetFilterBar
        countLabel={buildCountLabel(visibleCards.length, items.length, hasQuery)}
        query={query}
        selectedHeaderModel={selectedHeaderModel}
        onQueryChange={(value) => {
          startTransition(() => {
            setQuery(value);
          });
        }}
      />

      {items.length === 0 ? (
        <EmptyState
          className="self-start"
          message="Le moteur n'a retourné aucune mission exploitable dans la colonne principale pour cette exécution."
          title="Aucun camion actif avec tournée"
        />
      ) : visibleCards.length === 0 ? (
        <EmptyState
          className="self-start"
          message="Ajustez la recherche camion/commande ou effacez le filtre pour revoir tous les camions assignés."
          title="Aucun camion ne correspond au filtre"
        />
      ) : (
        <div
          className="grid min-h-0 auto-rows-max content-start gap-1 overflow-x-hidden overflow-y-auto overscroll-contain bg-transparent py-0.25 pr-1.25"
          onWheelCapture={(event) => {
            event.stopPropagation();
          }}
        >
          {visibleCards.map((card) => (
            <FleetCard
              key={card.assignmentId || card.assignment?.truck_id || card.assignment?.truck_name}
              {...card}
              routeCanvasStepsPanel={routeCanvasStepsPanel}
            />
          ))}
        </div>
      )}
    </div>,
    mountNode,
  );
}